import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/TypingTest.css'; // Your updated CSS file
import { getRandomParagraph } from '../data/sampleTexts';
import '../styles/TypingResults.css';
import TypingTestExtras from '../components/TypingTestExtras';
import { SeoManager, getPageSeo } from '../seo';
import FAQSection from '../components/FAQSection';
import { setItem } from '../utils/safeStorage';

const cloneWordUnits = (wordUnits) =>
  wordUnits.map((unit) => ({
    ...unit,
    chars: unit.chars.map((charObj) => ({ ...charObj })),
  }));

const getCommonPrefixLength = (left, right) => {
  const max = Math.min(left.length, right.length);
  let index = 0;
  while (index < max && left[index] === right[index]) {
    index += 1;
  }
  return index;
};

const applyBackspace = (progress) => {
  if (progress.charIndex > 0) {
    const newCharIdx = progress.charIndex - 1;
    const charObj = progress.wordUnits[progress.wordIndex]?.chars[newCharIdx];
    if (charObj) {
      if (charObj.status === 'incorrect') progress.mistakes = Math.max(0, progress.mistakes - 1);
      else if (charObj.status === 'correct') progress.correctCharacters = Math.max(0, progress.correctCharacters - 1);
      charObj.status = 'pending';
    }
    progress.charIndex = newCharIdx;
    progress.typedCharacters = Math.max(0, progress.typedCharacters - 1);
    return;
  }

  if (progress.wordIndex > 0) {
    const prevWordIdx = progress.wordIndex - 1;
    const prevWordUnit = progress.wordUnits[prevWordIdx];
    const prevCharIdx = prevWordUnit.chars.length - 1;
    const charObj = prevWordUnit?.chars[prevCharIdx];
    if (charObj) {
      if (charObj.status === 'incorrect') progress.mistakes = Math.max(0, progress.mistakes - 1);
      else if (charObj.status === 'correct') progress.correctCharacters = Math.max(0, progress.correctCharacters - 1);
      charObj.status = 'pending';
    }
    progress.wordIndex = prevWordIdx;
    progress.charIndex = prevCharIdx;
    progress.typedCharacters = Math.max(0, progress.typedCharacters - 1);
  }
};

const applyTypedChar = (progress, charTyped) => {
  const currentWordUnit = progress.wordUnits[progress.wordIndex];
  if (!currentWordUnit || progress.charIndex >= currentWordUnit.chars.length) {
    return { skipped: true, finished: false, incorrect: false };
  }

  const expectedCharObj = currentWordUnit.chars[progress.charIndex];
  progress.typedCharacters += 1;

  const isLastCharOfParagraph =
    progress.wordIndex === progress.wordUnits.length - 1 &&
    progress.charIndex === currentWordUnit.chars.length - 1;

  const incorrect = charTyped !== expectedCharObj.char;
  if (incorrect) {
    progress.mistakes += 1;
    expectedCharObj.status = 'incorrect';
  } else {
    progress.correctCharacters += 1;
    expectedCharObj.status = 'correct';
  }

  if (isLastCharOfParagraph) {
    return { skipped: false, finished: true, incorrect };
  }

  if (progress.charIndex + 1 < currentWordUnit.chars.length) {
    progress.charIndex += 1;
  } else if (progress.wordIndex + 1 < progress.wordUnits.length) {
    progress.wordIndex += 1;
    progress.charIndex = 0;
  }

  return { skipped: false, finished: false, incorrect };
};

const TypingTest = ({ duration = 60 }) => {
  const navigate = useNavigate();
  const [words, setWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  
  const [timer, setTimer] = useState(duration);
  const [maxTime, setMaxTime] = useState(duration);

  const [isActive, setIsActive] = useState(false);
  const [isPrestart, setIsPrestart] = useState(true); 
  const [startInstruction, setStartInstruction] = useState("Press Spacebar or Tap to start");

  const [showSpaceSymbol, setShowSpaceSymbol] = useState(false);
  const [allowDelete, setAllowDelete] = useState(true);
  const inputRef = useRef(null);
  const contentRef = useRef(null);
  const typingTextRef = useRef(null);
  const wordsRef = useRef([]);
  const progressRef = useRef({
    lastInputValue: '',
    wordIndex: 0,
    charIndex: 0,
    mistakes: 0,
    correctCharacters: 0,
    typedCharacters: 0,
  });
  const [level, setLevel] = useState('beginner');
  const activeCharRef = useRef(null);

  // State for custom dropdown
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const levelDropdownRef = useRef(null); // Ref for the dropdown container

  // State to prevent flicker before navigation
  const [isFinishing, setIsFinishing] = useState(false);

  const TARGET_VISUAL_SLOT_INDEX = 2;
  const levelOptions = ['beginner', 'intermediate', 'pro'];

  // Map duration to the correct config key
  const getConfigKey = (duration) => {
    switch (duration) {
      case 15: return '15s-cpm-typing-test';
      case 30: return '30s-cpm-typing-test';
      case 60: return '1min-cpm-typing-test';
      case 180: return '3min-cpm-typing-test';
      case 300: return '5min-cpm-typing-test';
      default: return '1min-cpm-typing-test'; // fallback
    }
  };

  const configKey = getConfigKey(duration);
  const seo = getPageSeo(configKey) || getPageSeo('1min-cpm-typing-test');
  const durationLabel = (() => {
    if (duration === 60) return '1 Minute';
    if (duration > 60) return `${duration / 60} Minute`;
    return `${duration} Second`;
  })();

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };
  
  useEffect(() => {
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
    if (isTouchDevice) {
      setStartInstruction("Tap to start");
    } else {
      setStartInstruction("Press Spacebar to start");
    }
  }, []);

  const startTest = useCallback(() => {
    if (!isPrestart) return; 
    setIsPrestart(false);
    setIsActive(true); 
    setTimeout(() => {
        inputRef.current?.focus();
    }, 50); 
  }, [isPrestart, setIsPrestart, setIsActive, inputRef]); 

  useEffect(() => {
    const handleStartKeyDown = (e) => {
      const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
      if (isPrestart && e.code === 'Space' && !isTouchDevice) {
        e.preventDefault(); 
        startTest();
      }
    };

    if (isPrestart) {
      document.addEventListener('keydown', handleStartKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleStartKeyDown);
    };
  }, [isPrestart, startTest]);

  // Close custom dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(event.target)) {
        setIsLevelDropdownOpen(false);
      }
    };
    if (isLevelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLevelDropdownOpen]);


  const resetTest = useCallback((newMaxTime = duration) => {
    setCurrentWordIndex(0);
    setCurrentCharIndex(0);
    setMaxTime(newMaxTime);
    setTimer(newMaxTime);
    setIsActive(false); 
    setIsPrestart(true); 
    setIsLevelDropdownOpen(false); // Close dropdown on reset
    setIsFinishing(false); // Reset finishing state
    progressRef.current = {
      lastInputValue: '',
      wordIndex: 0,
      charIndex: 0,
      mistakes: 0,
      correctCharacters: 0,
      typedCharacters: 0,
    };
    if (inputRef.current) {
        inputRef.current.value = '';
    }
  }, [duration]);

  const loadParagraph = useCallback(() => {
    let text = "";
    try {
        if (typeof getRandomParagraph === 'function') {
            text = getRandomParagraph(level);
        } else {
            text = "Default paragraph for typing practice. The quick brown fox jumps over the lazy dog.";
        }
        if (typeof text !== 'string') {
            text = "Fallback text due to unexpected data type. The quick brown fox jumps over the lazy dog.";
        }
    } catch (error) {
        text = "Error loading text. Please try again. The quick brown fox jumps over the lazy dog.";
    }

    const normalizedText = text.replace(/\s+/g, ' ').trim();
    const textWords = normalizedText.split(' ');

    const formattedWordUnits = textWords.map((wordStr, index) => {
      if (wordStr === "" && textWords.length === 1 && index === 0) return null;
      let charsForUnit = [...wordStr.split('').map(char => ({ char, status: 'pending' }))];
      if (index < textWords.length - 1) {
        charsForUnit.push({ char: ' ', status: 'pending' });
      }
      return { id: `${wordStr}-${index}`, chars: charsForUnit };
    }).filter(Boolean);

    wordsRef.current = formattedWordUnits;
    setWords(formattedWordUnits);
    if (typingTextRef.current) {
        typingTextRef.current.style.marginTop = '0px';
    }
    resetTest(duration); 
  }, [level, resetTest, duration]);

  const applyVibrateEffect = () => {
    if (contentRef.current) {
      contentRef.current.classList.add('vibrate');
      setTimeout(() => contentRef.current?.classList.remove('vibrate'), 150);
    }
  };

  const finishTyping = useCallback(() => {
    setIsActive(false);
    const durationUsed = maxTime - timer;
    const minutes = (durationUsed > 0 ? durationUsed : 1) / 60;
    const { correctCharacters: finalCorrectCharacters, mistakes: finalMistakes, typedCharacters: finalTypedCharacters } = progressRef.current;
    const wpm = Math.round((finalCorrectCharacters / 5) / minutes) || 0;
    const cpm = Math.round(finalCorrectCharacters / minutes) || 0;
    const totalAttemptsForAccuracy = finalCorrectCharacters + finalMistakes;
    const accuracy = totalAttemptsForAccuracy > 0 ? Math.round((finalCorrectCharacters / totalAttemptsForAccuracy) * 100) : 0;
    const results = { wpm, cpm, accuracy, mistakes: finalMistakes, totalChars: finalTypedCharacters, timeInSeconds: durationUsed };
    
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    const resultsPath = `${currentPath}/results`;

    setItem('typingResults', JSON.stringify(results));
    setItem('lastTestPath', currentPath);

    setIsFinishing(true); // Set state to true before navigating
    navigate(resultsPath, { state: { testResults: results } });
  }, [navigate, maxTime, timer, setIsActive]);

  const finishTypingRef = useRef(finishTyping);
  useEffect(() => {
    finishTypingRef.current = finishTyping;
  }, [finishTyping]);

  useEffect(() => {
    loadParagraph();
  }, [loadParagraph]); // loadParagraph has level in its deps, so this will run when level changes

  useEffect(() => {
    const handleClickToFocus = (event) => {
      if (isPrestart || event.target.closest('.settings') || event.target === inputRef.current || event.target.closest('.start-instruction-overlay')) {
        return;
      }
      inputRef.current?.focus();
    };
    document.addEventListener('click', handleClickToFocus);
    return () => document.removeEventListener('click', handleClickToFocus);
  }, [isPrestart]);

  useEffect(() => {
    let intervalId;
    if (isActive && timer > 0) {
      intervalId = setInterval(() => setTimer(prevTimer => prevTimer - 1), 1000);
    } else if (timer === 0 && isActive) { 
      finishTypingRef.current();
    }
    return () => clearInterval(intervalId);
  }, [isActive, timer]);

  useEffect(() => { 
    const activeCharEl = activeCharRef.current;
    const paragraphEl = typingTextRef.current;
    if (activeCharEl && paragraphEl && !isPrestart) { 
      let lineHeight = parseFloat(getComputedStyle(activeCharEl).lineHeight);
      if (isNaN(lineHeight) || lineHeight <= 0) {
        const fontSize = parseFloat(getComputedStyle(activeCharEl).fontSize);
        lineHeight = fontSize ? fontSize * 1.2 : 25;
      }
      if (isNaN(lineHeight) || lineHeight <= 0) lineHeight = 25;
      const activeCharScrollContainer = activeCharEl.closest('.react-word-unit') || activeCharEl;
      const activeCharPhysicalLine = Math.floor(activeCharScrollContainer.offsetTop / lineHeight);
      let newMarginTop = 0;
      if (activeCharPhysicalLine >= TARGET_VISUAL_SLOT_INDEX) {
        newMarginTop = -(activeCharPhysicalLine - TARGET_VISUAL_SLOT_INDEX) * lineHeight;
      }
      paragraphEl.style.marginTop = `${newMarginTop}px`;
    } else if (isPrestart && paragraphEl) {
        paragraphEl.style.marginTop = '0px'; 
    }
  }, [currentWordIndex, currentCharIndex, TARGET_VISUAL_SLOT_INDEX, words, isPrestart]);

  const handleInput = (e) => {
    if (isPrestart) return;

    const typedText = e.target.value;
    if (!isActive && typedText.length > 0 && timer > 0) {
      setIsActive(true);
    }
    if (timer === 0) {
      if (inputRef.current) inputRef.current.value = progressRef.current.lastInputValue;
      return;
    }

    const previousValue = progressRef.current.lastInputValue;
    const prefixLen = getCommonPrefixLength(previousValue, typedText);
    const deleteCount = previousValue.length - prefixLen;
    const inserted = typedText.slice(prefixLen);

    if (deleteCount === 0 && inserted.length === 0) return;

    if (deleteCount > 0 && !allowDelete) {
      if (inputRef.current) inputRef.current.value = previousValue;
      return;
    }

    const working = {
      wordIndex: progressRef.current.wordIndex,
      charIndex: progressRef.current.charIndex,
      mistakes: progressRef.current.mistakes,
      correctCharacters: progressRef.current.correctCharacters,
      typedCharacters: progressRef.current.typedCharacters,
      wordUnits: cloneWordUnits(wordsRef.current),
    };

    let hadIncorrect = false;
    let finished = false;
    let processedInserts = 0;

    for (let i = 0; i < deleteCount; i += 1) {
      applyBackspace(working);
    }

    for (let i = 0; i < inserted.length; i += 1) {
      const result = applyTypedChar(working, inserted[i]);
      if (result.skipped) break;
      processedInserts += 1;
      if (result.incorrect) hadIncorrect = true;
      if (result.finished) {
        finished = true;
        break;
      }
    }

    const nextValue = previousValue.slice(0, prefixLen) + inserted.slice(0, processedInserts);
    if (inputRef.current && nextValue !== typedText) {
      inputRef.current.value = nextValue;
    }

    progressRef.current = {
      lastInputValue: nextValue,
      wordIndex: working.wordIndex,
      charIndex: working.charIndex,
      mistakes: working.mistakes,
      correctCharacters: working.correctCharacters,
      typedCharacters: working.typedCharacters,
    };
    wordsRef.current = working.wordUnits;

    setCurrentWordIndex(working.wordIndex);
    setCurrentCharIndex(working.charIndex);
    setWords(working.wordUnits);

    if (hadIncorrect) applyVibrateEffect();
    if (finished) finishTypingRef.current();
  };

  const handleLevelSelect = (selectedLevel) => {
    setLevel(selectedLevel);
    setIsLevelDropdownOpen(false);
    // loadParagraph will be called by the useEffect that depends on `level`
  };

  const radius = 30; 
  const circumference = 2 * Math.PI * radius;
  const progressOffset = isPrestart ? 0 : circumference * (1 - (timer / maxTime)); 
  
  let timerColorClass = 'timer-default';
  if (!isPrestart) { 
    if (timer <= 5 && maxTime > 5) { 
        timerColorClass = 'timer-red';
    } else if (timer <= 10 && maxTime > 10) {
        timerColorClass = 'timer-yellow';
    }
  }

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={seo.structuredData}
      />
      <h1 className="typing-test-page-title">{durationLabel} Typing Test</h1>
      <div className="main">
        <div className={`wrapper ${showSpaceSymbol ? 'show-space-symbol' : ''}`}>
          <div className="settings">
            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={showSpaceSymbol} 
                onChange={(e) => setShowSpaceSymbol(e.target.checked)} 
                disabled={isActive && timer > 0} 
              />
              Show space symbol
            </label>
            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={allowDelete} 
                onChange={(e) => setAllowDelete(e.target.checked)} 
                disabled={isActive && timer > 0} 
              />
              Allow deleting characters
            </label>
            <div className="setting-item" ref={levelDropdownRef}>
              <span>Language Level:</span>
              <div 
                className={`custom-select-display ${isLevelDropdownOpen ? 'open' : ''}`}
                onClick={() => !(isActive && timer > 0) && setIsLevelDropdownOpen(!isLevelDropdownOpen)}
                onKeyDown={(e) => {
                  if (!(isActive && timer > 0) && (e.key === 'Enter' || e.key === ' ')) {
                    setIsLevelDropdownOpen(!isLevelDropdownOpen);
                  }
                }}
                role="button"
                tabIndex={ (isActive && timer > 0) ? -1 : 0}
                aria-haspopup="listbox"
                aria-expanded={isLevelDropdownOpen}
                aria-disabled={isActive && timer > 0}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
                <span className="custom-select-arrow"></span>
              </div>
              {isLevelDropdownOpen && !(isActive && timer > 0) && (
                <div className="custom-select-options" role="listbox">
                  {levelOptions.map(option => (
                    <div
                      key={option}
                      className={`custom-select-option ${level === option ? 'selected' : ''}`}
                      onClick={() => handleLevelSelect(option)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLevelSelect(option);}}
                      role="option"
                      tabIndex={0}
                      aria-selected={level === option}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="content-box" ref={contentRef}>
            <div className="typing-text"> 
              {isPrestart && (
                <div className="start-instruction-overlay" onClick={startTest} role="button" tabIndex={0} 
                     onKeyDown={(e) => { if(e.key === 'Enter' || e.key === ' ') startTest();}}
                >
                  <p>{startInstruction}</p>
                </div>
              )}
              <p 
                ref={typingTextRef} 
                className={`typing-text-paragraph ${isPrestart ? 'text-blurred' : ''}`}
                aria-hidden={isPrestart}
              >
                {words.map((wordUnit, wordUnitIdx) => (
                  <span key={wordUnit.id || `wu-${wordUnitIdx}`} className="react-word-unit">
                    {wordUnit.chars.map((charObj, charIdxInUnit) => {
                      const isCurrentActive = !isPrestart && wordUnitIdx === currentWordIndex && charIdxInUnit === currentCharIndex;
                      const isSpace = charObj.char === ' ';
                      return (
                        <span
                          key={`${wordUnit.id}-char-${charIdxInUnit}`}
                          ref={isCurrentActive ? activeCharRef : null}
                          className={
                            `char ${isPrestart ? 'pending' : charObj.status} ${isCurrentActive ? 'active' : ''} ${isSpace ? 'space-char' : ''}`.trim()
                          }
                        >
                          {isSpace ? (showSpaceSymbol ? '·' : '\u00A0') : charObj.char}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </p>
              <input
                ref={inputRef}
                type="text"
                className="input-field"
                onInput={handleInput}
                disabled={timer === 0 || isPrestart}
                autoFocus={!isPrestart}
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                lang="en"
                inputMode="text"
                aria-label="Typing input"
              />
            </div>
            <div className="result-details">
              <div className="timer-container">
                <svg className="timer-svg" width="80" height="80" viewBox="0 0 80 80">
                  <circle className="timer-ring-bg" cx="40" cy="40" r={radius} />
                  <circle
                    className={`timer-ring-progress ${isPrestart ? 'timer-default-stroke' : timerColorClass + '-stroke'}`}
                    cx="40" cy="40" r={radius}
                    strokeDasharray={circumference}
                    strokeDashoffset={progressOffset}
                  />
                </svg>
                <div className={`timer-text ${isPrestart ? 'timer-default' : timerColorClass}`}>
                  {isPrestart ? formatTime(maxTime) : formatTime(timer)}
                </div>
              </div>
            </div>
          </div>
          {(!isActive && timer === 0 && words.length > 0 && !isPrestart && !isFinishing) && (
            <button className="try-again-btn" onClick={() => resetTest(duration)} style={{marginTop: 24}}>Try Again</button>
          )}
        </div>
      </div>
      <TypingTestExtras duration={duration} />
      <FAQSection mode="typing" />
    </>
  );
};

export const ResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get test results from location state
  const testResults = location.state?.testResults;

  useEffect(() => {
    if (!testResults) {
      navigate('/');
    }
  }, [testResults, navigate]);

  const getDurationString = (duration) => {
    if (duration === 15) return '15s';
    if (duration === 30) return '30s';
    if (duration === 60) return '60s';
    if (duration === 180) return '3min';
    if (duration === 300) return '5min';
    return '15s';
  };

  if (!testResults) {
    return null;
  }

  return (
    <div className="results-main">
      <div className="results-wrapper">
        <h2>Typing Test Results</h2>
        <div className="results-grid">
          <div className="result-item">
            <h3>WPM</h3>
            <p className="big-number">{testResults.wpm || 0}</p>
            <p className="label">Words per minute</p>
          </div>
          <div className="result-item">
            <h3>CPM</h3>
            <p className="big-number">{testResults.cpm || 0}</p>
            <p className="label">Characters per minute</p>
          </div>
          <div className="result-item">
            <h3>Accuracy</h3>
            <p className="big-number">{testResults.accuracy || 0}%</p>
            <p className="label">Correct characters</p>
          </div>
          <div className="result-item">
            <h3>Mistakes</h3>
            <p className="big-number">{testResults.mistakes || 0}</p>
            <p className="label">Wrong characters</p>
          </div>
        </div>
        <button className="primary-btn" onClick={() => navigate(`/${getDurationString(testResults.duration)}-cpm-typing-test`)}>
          Try Again
        </button>
        <button className="secondary-btn" onClick={() => navigate('/')}>Back to Home</button>
      </div>
    </div>
  );
};

export { TypingTest };
